/**
 * @file ModeManager : mode courant, transitions (entree avec verification deblocage/ticket/cout, sortie, retour au Main), routage des commandes de mode.
 */
import type { ModeId } from '@shared/enums';
import { fail, okVoid } from '@shared/types';
import type { Result } from '@shared/types';
import type { ModeRules } from '@config/types';
import type { SimContext } from '../Context';
import { resolveAmounts } from '../economy/Wallet';
import { resolveControl, sameControl } from './ControlResolver';
import type { ModeCommand, ModeExitReason, ModeHud, ModeInstance, ModeStartParams } from './ModeTypes';
import { MODE_REGISTRY } from './registry';

export const HOME_MODE: ModeId = 'main';

export class ModeManager {
  private ctx: SimContext | null = null;
  private instance: ModeInstance | null = null;
  private readonly instances = new Map<ModeId, ModeInstance>();
  /** Regles du mode courant (avant le premier start : regles du Main). */
  rules: ModeRules;

  constructor(private readonly initialRules: ModeRules) {
    this.rules = initialRules;
  }

  bind(ctx: SimContext): void {
    this.ctx = ctx;
  }

  private get c(): SimContext {
    if (!this.ctx) throw new Error('ModeManager not bound');
    return this.ctx;
  }

  get current(): ModeInstance {
    if (!this.instance) throw new Error('No active mode');
    return this.instance;
  }

  get currentId(): ModeId {
    return this.instance?.id ?? HOME_MODE;
  }

  get knockback(): number {
    return this.instance?.knockback ?? 0;
  }

  get hasMode(): boolean {
    return this.instance !== null;
  }

  /** Une instance par mode, creee a la demande depuis le registre (l'etat de session du mode est reinitialise par enter). */
  private instanceOf(id: ModeId): ModeInstance {
    let inst = this.instances.get(id);
    if (!inst) {
      inst = MODE_REGISTRY.get(id)(this.c.config.modes[id]);
      this.instances.set(id, inst);
    }
    return inst;
  }

  /** Verifie l'acces (deblocage, ticket, cout) puis bascule. `free` saute ticket/cout (retour au Main, chargement). */
  start(id: ModeId, params: ModeStartParams = {}, free = false): Result {
    const ctx = this.c;
    const def = ctx.config.modes[id];
    if (!free) {
      if (!ctx.services.unlocks.isUnlocked(def.entry.unlock)) return fail('locked');
      const cost = resolveAmounts(def.entry.cost);
      if (!ctx.wallet.canAfford(cost)) return fail('notEnoughFunds');
      if (def.entry.ticket && ctx.services.tickets.count(def.entry.ticket) <= 0) return fail('noTickets');
      if (def.entry.ticket) ctx.services.tickets.consume(def.entry.ticket);
      ctx.wallet.spend(cost, `mode:${id}`);
    }
    if (this.instance) this.finish(this.instance.id === id ? 'reload' : 'switch');
    const inst = this.instanceOf(id);
    this.instance = inst;
    this.rules = def.rules;
    this.refreshControl();
    ctx.services.team.rebuild();
    inst.enter(ctx, params);
    ctx.bus.emit('ModeEntered', { mode: id });
    return okVoid;
  }

  /** Quitte le mode courant et revient au Main (gratuit). */
  returnHome(reason: ModeExitReason = 'quit'): void {
    if (this.instance?.id === HOME_MODE && reason !== 'rebirth') return;
    this.finish(reason);
    this.instance = null;
    this.start(HOME_MODE, {}, true);
  }

  private finish(reason: ModeExitReason): void {
    const inst = this.instance;
    if (!inst) return;
    const result = inst.exit(this.c, reason);
    this.c.bus.emit('ModeExited', { mode: inst.id, reason, result });
  }

  update(dt: number): void {
    this.refreshControl();
    this.instance?.update(this.c, dt);
  }

  handleCommand(cmd: ModeCommand): Result {
    if (!this.instance) return fail('wrongMode');
    return this.instance.handleCommand(this.c, cmd);
  }

  getHud(): ModeHud | null {
    return this.instance ? this.instance.getHud(this.c) : null;
  }

  /** Recalcule le controle effectif (les reglages joueur peuvent changer a tout moment). */
  refreshControl(): void {
    const ctx = this.c;
    const next = resolveControl(this.rules.control, ctx.data.settings);
    if (!sameControl(next, ctx.rt.control)) {
      ctx.rt.control = next;
      ctx.bus.emit('ControlChanged', { control: next });
    }
  }

  /** Utilise par les tests : regles initiales. */
  get defaultRules(): ModeRules {
    return this.initialRules;
  }
}
